import {
  Button,
  IconButton,
  TextCaption,
  TextTitle,
} from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';
import { FC, Fragment, useEffect, useRef, useState } from 'react';

import { useProStudioViewerRole } from '../../hooks';
import { agentSettingsStore } from '../../stores/agent-settings-store';
import { useAgentExtras } from './agent-extras';
import { tableStore } from '../../stores/table-store';
import { buildCanvasEdges } from '../canvas-view/canvas-edges-model';
import { buildCanvasAgentNodes } from '../canvas-view/canvas-view-model';
import {
  KNOWLEDGE_CHOICES,
  type KnowledgeSettings,
  SETTING_LABEL,
  SUPERVISOR_CHOICES,
  type SupervisorSettings,
  type AgentKey,
} from './agent-settings-model';
import { useAgentSettings } from './use-agent-settings';
import { saveExtrasBeforeSettings } from './agent-settings-write';
import { findModelNode } from './agent-prompt';
import { LangfusePromptCards } from './langfuse-prompt-cards';

import './agent-settings-panel.scss';

/**
 * Agent settings (Bobcat prototype): what the Supervisor and the Knowledge
 * Agent do, in plain choices. The prompts are written from these
 * (`agent-settings-model.ts`); the model settings stay in the Supervisor panel.
 */

const AGENT_TITLE: Record<AgentKey, string> = {
  supervisor: 'Supervisor Agent',
  knowledge: 'Knowledge Agent',
};

type Draft = {
  supervisor: SupervisorSettings;
  knowledge: KnowledgeSettings;
  /** Each agent's prompt, without the settings block; null with no node. */
  prompts: Record<AgentKey, string | null>;
};

const modelNode = (agent: AgentKey) => findModelNode(tableStore.rows, agent);

const Section: FC<{
  title: string;
  sub?: string;
  children: React.ReactNode;
}> = ({ title, sub, children }) => (
  <section className="c-agent-settings__sec">
    <h3 className="c-agent-settings__sec-title">{title}</h3>
    {sub && <p className="c-agent-settings__sec-sub">{sub}</p>}
    {children}
  </section>
);

/**
 * `embedded`: inside the AI dock's Agents tab, which has its own header and
 * close — so no title or close of its own, and it fills the dock.
 */
export const AgentSettingsPanel: FC<{ embedded?: boolean }> = observer(
  ({ embedded = false }) => {
    const isViewer = useProStudioViewerRole();
    const extras = useAgentExtras(isViewer);
    const settings = useAgentSettings();
    // The use-case agents the canvas draws, and the intents that route to each.
    const rows = tableStore.getAllSortedRows();
    const edges = buildCanvasEdges(rows);
    const routes = buildCanvasAgentNodes(rows)
      .filter((a) => a.category !== 'core')
      .map((a) => ({
        id: a.id,
        title: a.title,
        intents: [
          ...new Set(
            edges
              .filter(
                (e) => e.target === a.id && e.kind === 'intent' && e.label
              )
              .map((e) => e.label as string)
          ),
        ],
      }));
    const { tab, flash } = agentSettingsStore;

    const loaded: Draft = {
      supervisor: settings.supervisor,
      knowledge: settings.knowledge,
      prompts: settings.prompts,
    };
    const loadedKey = JSON.stringify(loaded);
    const [draft, setDraft] = useState<Draft>(loaded);
    // A save, or a change the assistant applied, replaces what was loaded.
    useEffect(() => {
      setDraft(JSON.parse(loadedKey) as Draft);
    }, [loadedKey]);
    const settingsDirty = JSON.stringify(draft) !== loadedKey;
    const dirty = settingsDirty || extras.dirty;
    const saving = settings.saving || extras.saving;
    const saveAll = () =>
      saveExtrasBeforeSettings({
        extras: extras.dirty ? extras.save : undefined,
        // Only the agent that changed: saving the other would add its default
        // block to text the designer never touched.
        settings: settingsDirty
          ? () =>
              settings.save({
                ...(JSON.stringify(draft.supervisor) !==
                JSON.stringify(loaded.supervisor)
                  ? { supervisor: draft.supervisor }
                  : {}),
                ...(JSON.stringify(draft.knowledge) !==
                JSON.stringify(loaded.knowledge)
                  ? { knowledge: draft.knowledge }
                  : {}),
                prompts: Object.fromEntries(
                  (['supervisor', 'knowledge'] as const)
                    .filter(
                      (a) =>
                        draft.prompts[a] !== null &&
                        draft.prompts[a] !== loaded.prompts[a]
                    )
                    .map((a) => [a, draft.prompts[a] as string])
                ),
              })
          : undefined,
      });

    // Point at what just changed, once.
    const body = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (!flash.length) return undefined;
      const els = flash
        .map((key) => body.current?.querySelector(`[data-setting="${key}"]`))
        .filter((el): el is Element => !!el);
      els.forEach((el) => {
        el.classList.remove('is-changed');
        void (el as HTMLElement).offsetWidth;
        el.classList.add('is-changed');
      });
      els[0]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const done = setTimeout(() => agentSettingsStore.clearFlash(), 2600);
      return () => clearTimeout(done);
    }, [flash, tab]);

    const set = <A extends AgentKey>(
      agent: A,
      key: keyof Draft[A],
      value: Draft[A][keyof Draft[A]]
    ) =>
      setDraft((prev) => ({
        ...prev,
        [agent]: { ...prev[agent], [key]: value },
      }));

    const choice = <A extends AgentKey>(
      agent: A,
      key: keyof Draft[A] & string,
      options: readonly string[]
    ) => (
      <div className="c-agent-settings__choices" data-setting={key}>
        {options.map((o) => {
          const on = draft[agent][key] === o;
          return (
            <label
              key={o}
              className={`c-agent-settings__choice${on ? ' is-on' : ''}`}
            >
              <input
                type="radio"
                name={`${agent}-${key}`}
                checked={on}
                disabled={isViewer}
                onChange={() => set(agent, key, o as Draft[A][keyof Draft[A]])}
              />
              {o}
            </label>
          );
        })}
      </div>
    );

    const toggle = <A extends AgentKey>(
      agent: A,
      key: keyof Draft[A] & keyof typeof SETTING_LABEL,
      help?: string
    ) => (
      <label className="c-agent-settings__switch" data-setting={key}>
        <span>
          {SETTING_LABEL[key]}
          {help && <small>{help}</small>}
        </span>
        <input
          type="checkbox"
          checked={!!draft[agent][key]}
          disabled={isViewer}
          onChange={(e) =>
            set(agent, key, e.target.checked as Draft[A][keyof Draft[A]])
          }
        />
      </label>
    );

    const supervisor = (
      <>
        <Section
          title="How it routes"
          sub="Each customer message goes to the agent whose intent it matches best. Edit the routes on the canvas."
        >
          {routes.length === 0 ? (
            <p className="c-agent-settings__none">
              No use-case agents yet — everything goes to the Knowledge Agent.
            </p>
          ) : (
            routes.slice(0, 6).map((r) => (
              <div key={r.id} className="c-agent-settings__route">
                <b>{r.title}</b>
                <span>
                  {r.intents.length
                    ? `Intents: ${r.intents.slice(0, 4).join(', ')}`
                    : 'Reached from another agent’s flow'}
                </span>
              </div>
            ))
          )}
          {routes.length > 6 && (
            <p className="c-agent-settings__none">
              and {routes.length - 6} more use-case agents on the canvas
            </p>
          )}
          <div className="c-agent-settings__route">
            <b>Knowledge Agent</b>
            <span>Everything else it can answer from its sources</span>
          </div>
        </Section>
        <Section title={SETTING_LABEL.clarify}>
          {choice('supervisor', 'clarify', SUPERVISOR_CHOICES.clarify)}
        </Section>
        <Section
          title="When it hands off"
          sub="Where it goes is the live agent platform on each channel."
        >
          {toggle('supervisor', 'handoffAsk')}
          <p className="c-agent-settings__label">
            {SETTING_LABEL.handoffUpset}
          </p>
          {choice(
            'supervisor',
            'handoffUpset',
            SUPERVISOR_CHOICES.handoffUpset
          )}
          <label
            className="c-agent-settings__switch"
            data-setting="handoffFails"
          >
            <span>
              {SETTING_LABEL.handoffFails}
              <small>When it can’t help with the same request</small>
            </span>
            <select
              value={draft.supervisor.handoffFails}
              disabled={isViewer}
              onChange={(e) =>
                set(
                  'supervisor',
                  'handoffFails',
                  e.target.value as SupervisorSettings['handoffFails']
                )
              }
            >
              {SUPERVISOR_CHOICES.handoffFails.map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </label>
        </Section>
        <Section title={SETTING_LABEL.outOfScope}>
          {choice('supervisor', 'outOfScope', SUPERVISOR_CHOICES.outOfScope)}
        </Section>
        <Section title="Small talk">
          {toggle(
            'supervisor',
            'chitchat',
            'Replies briefly to greetings and thanks, then steers back'
          )}
        </Section>
      </>
    );

    const knowledge = (
      <>
        <Section title="Sources" sub="What its answers come from.">
          {settings.files.length === 0 ? (
            <p className="c-agent-settings__none">
              No documents yet — add them under Edit solution.
            </p>
          ) : (
            settings.files.map((file) => (
              <div
                key={file.id ?? file.originalName}
                className="c-agent-settings__route"
              >
                <b>{file.originalName}</b>
                <span>{file.mimeType}</span>
              </div>
            ))
          )}
        </Section>
        <Section title="How it answers">
          <p className="c-agent-settings__label">{SETTING_LABEL.length}</p>
          {choice('knowledge', 'length', KNOWLEDGE_CHOICES.length)}
          {toggle('knowledge', 'cite', 'Adds the article it used')}
          {toggle(
            'knowledge',
            'strict',
            'Never fills a gap from general knowledge'
          )}
          {toggle('knowledge', 'disclaimer', 'In its first answer')}
        </Section>
        <Section title={SETTING_LABEL.noAnswer}>
          {choice('knowledge', 'noAnswer', KNOWLEDGE_CHOICES.noAnswer)}
        </Section>
      </>
    );

    return (
      <aside
        className={`c-agent-settings${embedded ? ' c-agent-settings--embedded' : ''}`}
        aria-label="Agent settings"
        data-testid="bobcat-agent-settings"
      >
        {!embedded && (
          <div className="c-agent-settings__head">
            <div>
              <TextTitle size="xsmall">Agent settings</TextTitle>
              <TextCaption variant="secondary">
                What the two core agents do. Their prompts are written from
                these.
              </TextCaption>
            </div>
            <IconButton
              name="close"
              size="small"
              variant="tertiary"
              aria-label="Close"
              onClick={() => agentSettingsStore.close()}
            />
          </div>
        )}

        <div className="c-agent-settings__tabs" role="tablist">
          {(['supervisor', 'knowledge'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`c-agent-settings__tab${tab === t ? ' is-on' : ''}`}
              onClick={() => agentSettingsStore.setTab(t)}
            >
              {AGENT_TITLE[t]}
            </button>
          ))}
        </div>

        <div className="c-agent-settings__body" ref={body}>
          {!settings.loaded ? (
            <p className="c-agent-settings__none">Loading the solution…</p>
          ) : (
            <Fragment key={tab}>
              {tab === 'supervisor' ? supervisor : knowledge}
              {tab === 'supervisor' ? (
                <Section
                  title="Tasks and example phrases"
                  sub="What the Supervisor routes to, and phrases customers might use for each."
                >
                  {extras.supervisor}
                </Section>
              ) : (
                <Section
                  title="Documents"
                  sub="What the Knowledge Agent answers from."
                >
                  {extras.knowledge}
                </Section>
              )}
              <Section
                title="Prompt"
                sub={
                  draft.prompts[tab] === null
                    ? undefined
                    : `What the ${AGENT_TITLE[tab]} is told on every message. The choices above are added to the end of it when you save.`
                }
              >
                {draft.prompts[tab] === null ? (
                  <p className="c-agent-settings__none">
                    This solution has no {AGENT_TITLE[tab]} model step with a
                    prompt to edit.
                  </p>
                ) : (
                  <textarea
                    className="c-agent-settings__prompt"
                    data-testid={`bobcat-agent-settings-prompt-${tab}`}
                    value={draft.prompts[tab] ?? ''}
                    disabled={isViewer}
                    rows={12}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        prompts: { ...d.prompts, [tab]: e.target.value },
                      }))
                    }
                  />
                )}
                <LangfusePromptCards
                  agent={tab}
                  isViewer={isViewer}
                  busy={dirty}
                />
              </Section>
              <Section title="Model">
                <p className="c-agent-settings__none">
                  {settings.model}
                  {settings.temperature !== undefined &&
                    settings.temperature !== null &&
                    ` · temperature ${settings.temperature}`}{' '}
                  ·{' '}
                  {modelNode(tab) && (
                    <button
                      type="button"
                      className="c-agent-settings__link"
                      onClick={() => {
                        const node = modelNode(tab);
                        if (node) tableStore.openNodeOnCanvas(node.id);
                      }}
                    >
                      Edit on the canvas (#{modelNode(tab)?.nodeNumber})
                    </button>
                  )}
                </p>
              </Section>
            </Fragment>
          )}
        </div>

        {!isViewer && (
          <div className="c-agent-settings__foot">
            <Button
              size="small"
              variant="ghost"
              disabled={!dirty || saving}
              onClick={() => {
                setDraft(JSON.parse(loadedKey) as Draft);
                extras.reset();
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              disabled={!dirty || saving}
              test-id="bobcat-agent-settings-save"
              onClick={() => void saveAll()}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </aside>
    );
  }
);
